import { Paper, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { useId, useState } from "react";

import type { UsageRow } from "../../api/generated/model";
import { formatCount } from "../../ui/format";
import { formatUsd, formatUtcDay, formatUtcShortDay } from "./usage-format";
import { daysBefore } from "./usage-search";
import classes from "./usage.module.css";

/** The summary row: the page's total in four figures (the owner's v1 mockup, 2026-10-28). */
export function UsageSummary({ total }: { total: UsageRow }) {
  const settled = total.succeeded + total.failed;
  const cost = total.vendor_cost_usd;
  return (
    <SimpleGrid component="section" cols={{ base: 2, md: 4 }} aria-label="Summary">
      <Figure
        label="Generations"
        value={formatCount(total.generations)}
        note={`${formatCount(total.jobs)} jobs with their steps`}
      />
      <Figure
        label="Net credits"
        value={formatCount(total.net)}
        note={`${formatCount(total.refunded)} refunded`}
      />
      <Figure
        label="Vendor cost"
        value={cost === null ? (total.jobs === 0 ? "—" : "unknown") : formatUsd(cost)}
        note={
          total.cost_incomplete === 0
            ? "every job costed"
            : `${formatCount(total.cost_incomplete)} jobs not fully costed`
        }
      />
      <Figure
        label="Failed"
        value={settled === 0 ? "—" : `${((100 * total.failed) / settled).toFixed(1)}%`}
        note={`${formatCount(total.failed)} of ${formatCount(settled)} settled generations`}
      />
    </SimpleGrid>
  );
}

function Figure({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Text size="sm" c="var(--cai-color-text-muted)">
        {label}
      </Text>
      <Text fz={26} fw={600} lh={1.3} c="var(--cai-color-text-heading)">
        {value}
      </Text>
      <Text size="xs" c="var(--cai-color-text-muted)">
        {note}
      </Text>
    </Paper>
  );
}

interface Point {
  day: string;
  value: number;
  /** The value as the tooltip and the table view say it. */
  text: string;
}

/** Every UTC day from `from` to `to`; the API holds a range to 366 days. */
function everyDay(from: string, to: string): string[] {
  const days: string[] = [];
  for (let day = from; day <= to && days.length < 366; day = daysBefore(day, -1)) days.push(day);
  return days;
}

const costText = (row: UsageRow | undefined) =>
  row === undefined ? "$0.00" : row.vendor_cost_usd === null ? "unknown" : formatUsd(row.vendor_cost_usd);

/**
 * Net credits and vendor cost per day over the filters' days (the owner's v1 mockup): the API's
 * `group_by=day` page, a day with no row drawn as 0. Each column says its value on hover, and the
 * table view lists every day, newest first.
 */
export function DailyCharts({ from, to, rows }: { from: string; to: string; rows: UsageRow[] }) {
  const [table, setTable] = useState(false);
  const byDay = new Map(rows.map((row) => [row.key, row]));
  const days = everyDay(from, to).map((day) => ({ day, row: byDay.get(day) }));
  const net = days.map(({ day, row }) => {
    const value = row?.net ?? 0;
    return { day, value, text: `${formatCount(value)} credits` };
  });
  const cost = days.map(({ day, row }) => ({
    day,
    value: Number(row?.vendor_cost_usd ?? 0),
    text: costText(row),
  }));
  return (
    <Stack gap="xs">
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <DayChart title="Net credits per day" points={net} money={false} />
        <DayChart title="Vendor cost per day" note="USD, known costs" points={cost} money />
      </SimpleGrid>
      <details onToggle={(event) => setTable(event.currentTarget.open)}>
        <Text component="summary" size="sm" c="var(--cai-color-text-link)">
          Table view of both charts
        </Text>
        {table ? (
          <Table.ScrollContainer minWidth={300}>
            <Table maw={480}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Day (UTC)</Table.Th>
                  <Table.Th className={classes.num}>Net credits</Table.Th>
                  <Table.Th className={classes.num}>Vendor cost</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {days.toReversed().map(({ day, row }) => (
                  <Table.Tr key={day}>
                    <Table.Td>{formatUtcDay(day)}</Table.Td>
                    <Table.Td className={classes.num}>{formatCount(row?.net ?? 0)}</Table.Td>
                    <Table.Td className={classes.num}>{costText(row)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : null}
      </details>
    </Stack>
  );
}

const W = 560;
const H = 196;
const LEFT = 46;
const RIGHT = 6;
const TOP = 10;
const BOTTOM = 24;

/** A round step for about three gridlines up to `max`: 1, 2, 2.5 or 5 times a power of ten. */
function niceStep(max: number): number {
  const target = max / 3;
  const power = 10 ** Math.floor(Math.log10(target));
  return ([1, 2, 2.5, 5].find((each) => each * power >= target) ?? 10) * power;
}

/** An axis value in a few characters: 0, 750, 1.5K; $0.5, $12, $1.2K. */
function compact(value: number, money: boolean): string {
  const text =
    value >= 1000 ? `${+(value / 1000).toFixed(1)}K` : String(+value.toFixed(value < 10 ? 1 : 0));
  return money ? `$${text}` : text;
}

/** A column from the baseline up, its top's corners rounded by 4px. */
function column(x: number, width: number, top: number, base: number): string {
  const r = Math.min(4, width / 2, base - top);
  const right = x + width;
  return `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${right - r}Q${right},${top} ${right},${top + r}V${base}Z`;
}

function DayChart(props: { title: string; note?: string; points: Point[]; money: boolean }) {
  const { title, note, points, money } = props;
  const [active, setActive] = useState<number | null>(null);
  const titleId = useId();
  const plotW = W - LEFT - RIGHT;
  const plotH = H - TOP - BOTTOM;
  // A floor keeps a quiet range's few credits or cents from filling the whole height.
  const most = Math.max(...points.map((point) => point.value), money ? 1 : 10);
  const step = niceStep(most);
  const ceiling = Math.ceil(most / step) * step;
  const y = (value: number) => TOP + plotH - (Math.max(0, value) / ceiling) * plotH;
  const slot = plotW / Math.max(1, points.length);
  const width = Math.min(24, Math.max(1, slot - 2));
  const ticks = Array.from({ length: Math.round(ceiling / step) + 1 }, (_, at) => at * step);
  // About six day labels, the last day always among them.
  const every = Math.ceil(points.length / 6);
  const shown = active === null ? undefined : points[active];
  return (
    <figure className={classes.chart} aria-labelledby={titleId}>
      <Stack gap={0} component="figcaption">
        <Title id={titleId} order={3} size="h5">
          {title}
        </Title>
        <Text size="xs" c="var(--cai-color-text-muted)">
          {[note, `${points.length} days`].filter(Boolean).join(" · ")}
        </Text>
      </Stack>
      <div style={{ position: "relative" }}>
        {/* The figure is named by its caption; the table view lists every value. */}
        <svg viewBox={`0 0 ${W} ${H}`} aria-hidden onPointerLeave={() => setActive(null)}>
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                className={tick === 0 ? classes.base : classes.grid}
                x1={LEFT}
                x2={W - RIGHT}
                y1={y(tick)}
                y2={y(tick)}
              />
              <text className={classes.axis} x={LEFT - 8} y={y(tick) + 4} textAnchor="end">
                {compact(tick, money)}
              </text>
            </g>
          ))}
          {points.map((point, at) => {
            const x = LEFT + at * slot;
            const base = TOP + plotH;
            return (
              <g key={point.day}>
                {point.value > 0 ? (
                  <path
                    className={classes.bar}
                    data-active={active === at ? "" : undefined}
                    d={column(x + (slot - width) / 2, width, y(point.value), base)}
                  />
                ) : null}
                {(points.length - 1 - at) % every === 0 ? (
                  <text className={classes.axis} x={x + slot / 2} y={H - 6} textAnchor="middle">
                    {formatUtcShortDay(point.day)}
                  </text>
                ) : null}
                {/* The whole slot answers the pointer, wider and taller than its column. */}
                <rect
                  x={x}
                  y={TOP}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  onPointerEnter={() => setActive(at)}
                />
              </g>
            );
          })}
        </svg>
        {shown === undefined || active === null ? null : (
          <div
            className={classes.tip}
            style={{ left: `${Math.min(88, Math.max(12, ((LEFT + (active + 0.5) * slot) / W) * 100))}%` }}
          >
            <strong>{shown.text}</strong>
            {formatUtcDay(shown.day)}
          </div>
        )}
      </div>
    </figure>
  );
}
